import {
  NoteTypeCreateDocument,
  NoteTypeDeleteDocument,
  NoteTypeGetByIdDocument,
  NoteTypeListDocument,
  NoteTypesPaginatedDocument,
  NoteTypeUpdateDocument,
} from "api/generated";
import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { ResourceEnum, ScopeEnum } from "config";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

import {
  ICreateNoteTypeVars,
  IGetNotesListArgs,
  IGetNoteTypeByIdReturnType,
  INotesListReturnType,
  INoteTypeModel,
  IUpdateNoteTypeVars,
} from "api/services/library/note-type";

export async function getNotesTypeList(
  variables: IGetNotesListArgs
): Promise<INotesListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: NoteTypesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.noteTypesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getNotesTypesForFilters(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<Pick<INoteTypeModel, "noteTypeId" | "name">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: NoteTypeListDocument,
      variables: {
        resource,
        scope,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.noteTypeList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function createNoteType(
  variables: ICreateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: NoteTypeCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all note type queries so they refetch with the new item
    executeEviction(client, {
      typename: "NoteType",
      fieldNames: ["noteTypesPaginated", "noteTypeList"],
    });

    return res?.data?.noteTypeCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getNoteTypeById(
  noteTypeId: number
): Promise<IGetNoteTypeByIdReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: NoteTypeGetByIdDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.noteTypeGetById;
  } catch (error) {
    throw error;
  }
}

export async function updateNoteTypeById(
  variables: IUpdateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: NoteTypeUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "NoteType",
      id: variables.noteTypeId.toString(),
      fieldNames: ["noteTypesPaginated", "noteTypeList", "noteTypeGetById"],
    });

    return res?.data?.noteTypeUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteNoteType(noteTypeId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: NoteTypeDeleteDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "NoteType",
      id: noteTypeId.toString(),
      fieldNames: ["noteTypesPaginated", "noteTypeList", "noteTypeGetById"],
    });

    return Boolean(res?.data?.noteTypeDelete);
  } catch (e) {
    throw e;
  }
}
